// Santé du radar (spec § 13) : les quatorze dernières nuits du worker, les sources à regarder et l'entonnoir des
// recherches de contacts (sociétés → site → personnes → e-mail sûr) par source et par pays. Réservé au responsable des
// imports par la base (politiques de `job_runs`, `source_runs`, `contact_search_steps`, lues par `radar_health`).
import { useQuery } from "@tanstack/react-query";
import { useLocaleState, useTranslate } from "ra-core";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { countryName, formatDay } from "./projectUtils";
import {
  decliningSources,
  FUNNEL_STEPS,
  funnelRows,
  nights,
  runCounters,
  type Health,
  type RunStatus,
} from "./radarHealth";
import { useIsImportsOwner } from "./useIsImportsOwner";

const NIGHTS = 14;
const SEARCH_DAYS = 30;

const STATUS_CLASS: Record<RunStatus, string> = {
  ok: "text-muted-foreground",
  partial: "text-amber-700 dark:text-amber-400",
  error: "text-destructive",
};

// Étapes dans l'ordre où la recherche de contacts les parcourt.
const STEP_ORDER = [
  "summary",
  "buyer_notice",
  "worldbank_team",
  "site",
  "site_people",
  "linkedin",
  "project_people",
  "email",
  "chain",
  "lookup",
];
const stepRank = (step: string) =>
  STEP_ORDER.includes(step) ? STEP_ORDER.indexOf(step) : STEP_ORDER.length;

export const RadarHealthPage = () => {
  const translate = useTranslate();
  const t = (key: string, options?: object) =>
    translate(`resources.projects.health.${key}`, options);
  const [locale = "fr"] = useLocaleState();
  const [by, setBy] = useState<"source" | "country">("source");
  const owner = useIsImportsOwner();
  const health = useQuery({
    queryKey: ["radar_health"],
    enabled: owner.data === true,
    refetchOnMount: "always",
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc("radar_health", {
        nights: NIGHTS,
        search_days: SEARCH_DAYS,
      });
      if (error) throw error;
      return data as Health;
    },
  });

  if (owner.isPending) return null;
  if (!owner.data) {
    return (
      <p className="mt-4 text-sm text-muted-foreground">{t("forbidden")}</p>
    );
  }

  const number = (value: number) => value.toLocaleString(locale);
  const dollars = (value: number) =>
    value.toLocaleString(locale, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  const percent = (value: number) =>
    value.toLocaleString(locale, {
      style: "percent",
      maximumFractionDigits: 0,
    });
  // Libellé connu, sinon le code tel que le worker l'écrit.
  const label = (group: string, key: string) =>
    translate(`resources.projects.health.${group}.${key}`, { _: key });

  const data = health.data;
  const allNights = data ? nights(data.runs, data.contacts_runs) : [];
  const declining = data ? decliningSources(data.sources) : [];
  const funnel = data ? funnelRows(data.funnel, by) : [];
  const steps = data
    ? [...new Set(data.outcomes.map((o) => o.step))]
        .sort((a, b) => stepRank(a) - stepRank(b))
        .map((step) => ({
          step,
          outcomes: data.outcomes.filter((o) => o.step === step),
          rejected: data.rejected.filter((r) => r.step === step),
        }))
    : [];

  return (
    <div className="mt-2 pb-8 flex flex-col gap-8">
      <div>
        <h2 className="text-xl font-semibold">{t("title")}</h2>
        <p className="text-sm text-muted-foreground max-w-3xl">{t("intro")}</p>
      </div>
      {health.error ? (
        <p className="text-sm text-destructive">{health.error.message}</p>
      ) : health.isPending ? null : (
        <>
          <section className="flex flex-col gap-2">
            <h3 className="font-medium">
              {t("nights_title", { count: NIGHTS })}
            </h3>
            {!allNights.length ? (
              <p className="text-sm text-muted-foreground">
                {t("nights_empty")}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("night")}</TableHead>
                    <TableHead>{t("commands")}</TableHead>
                    <TableHead className="text-right">
                      {t("contact_requests")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("llm_cost")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("provider_cost")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allNights.map((night) => (
                    <TableRow key={night.day} className="align-top">
                      <TableCell className="whitespace-nowrap">
                        <div>{formatDay(night.day, locale)}</div>
                        <div
                          className={cn("text-xs", STATUS_CLASS[night.status])}
                        >
                          {t(`status.${night.status}`)}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-normal">
                        <div className="flex flex-wrap gap-1">
                          {night.runs.map((run, index) => (
                            <Badge
                              key={index}
                              variant="outline"
                              className={cn(
                                "font-mono",
                                STATUS_CLASS[run.status],
                              )}
                              title={`${new Date(run.started_at).toLocaleTimeString(locale, { timeStyle: "short" })} · ${number(run.seconds)} s`}
                            >
                              {run.command}
                            </Badge>
                          ))}
                        </div>
                        <ul className="mt-1 text-xs text-muted-foreground">
                          {night.runs.map((run, index) => {
                            const counters = runCounters(run);
                            return counters.length ? (
                              <li key={index}>
                                <span className="font-mono">{run.command}</span>
                                {" : "}
                                {counters
                                  .map(
                                    ([key, value]) =>
                                      `${number(value)} ${label("counters", key)}`,
                                  )
                                  .join(" · ")}
                              </li>
                            ) : null;
                          })}
                        </ul>
                        {night.errors.length ? (
                          <ul className="mt-1 text-xs text-muted-foreground">
                            {night.errors.slice(0, 4).map((error, index) => (
                              <li key={index} className="break-words">
                                {error}
                              </li>
                            ))}
                            {night.errors.length > 4 ? (
                              <li>
                                {t("more_errors", {
                                  count: night.errors.length - 4,
                                })}
                              </li>
                            ) : null}
                          </ul>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {night.contacts ? number(night.contacts.requests) : "—"}
                        {night.contacts?.failed ? (
                          <div className="text-xs text-destructive">
                            {t("failed_runs", { count: night.contacts.failed })}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {dollars(night.llm)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {dollars(night.provider)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="font-medium">{t("sources_title")}</h3>
            <p className="text-sm text-muted-foreground max-w-3xl">
              {t("sources_intro")}
            </p>
            {!declining.length ? (
              <p className="text-sm text-muted-foreground">
                {t("sources_empty")}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("source")}</TableHead>
                    <TableHead>{t("reason")}</TableHead>
                    <TableHead className="text-right">
                      {t("inserted")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("kept_before")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("kept_recent")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {declining.map((source) => (
                    <TableRow key={source.source} className="align-top">
                      <TableCell className="whitespace-normal">
                        <div>{source.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">
                          {source.source}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-normal">
                        <div>{t(`reasons.${source.reason}`)}</div>
                        {source.reason === "failing" && source.last_error ? (
                          <div className="text-xs text-muted-foreground break-words">
                            {source.last_error}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {number(source.inserted)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {number(source.kept_before ?? 0)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {number(source.kept_recent ?? 0)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-medium">
                {t("funnel_title", { count: SEARCH_DAYS })}
              </h3>
              <div className="flex gap-1">
                {(["source", "country"] as const).map((key) => (
                  <Button
                    key={key}
                    size="sm"
                    variant={by === key ? "secondary" : "ghost"}
                    onClick={() => setBy(key)}
                  >
                    {t(`by_${key}`)}
                  </Button>
                ))}
              </div>
            </div>
            <p className="text-sm text-muted-foreground max-w-3xl">
              {t("funnel_intro")}
            </p>
            {!funnel.length ? (
              <p className="text-sm text-muted-foreground">
                {t("funnel_empty")}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t(by)}</TableHead>
                    <TableHead className="text-right">
                      {t("projects")}
                    </TableHead>
                    {FUNNEL_STEPS.map((step) => (
                      <TableHead key={step} className="text-right">
                        {t(`steps.${step}`)}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {funnel.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell className="whitespace-nowrap">
                        {by === "country" && row.key !== "?"
                          ? countryName(row.key, locale)
                          : row.key}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {number(row.projects)}
                      </TableCell>
                      {FUNNEL_STEPS.map((step) => (
                        <TableCell
                          key={step}
                          className="text-right tabular-nums"
                        >
                          {number(row.counts[step])}
                          <span className="ml-1 text-xs text-muted-foreground">
                            {percent(row.share[step])}
                          </span>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="font-medium">
              {t("outcomes_title", { count: SEARCH_DAYS })}
            </h3>
            <p className="text-sm text-muted-foreground max-w-3xl">
              {t("outcomes_intro")}
            </p>
            {!steps.length ? (
              <p className="text-sm text-muted-foreground">
                {t("funnel_empty")}
              </p>
            ) : (
              <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                {steps.map(({ step, outcomes, rejected }) => (
                  <div key={step} className="flex flex-col gap-1 text-sm">
                    <div className="font-medium border-b pb-1">
                      {label("step_names", step)}
                    </div>
                    {outcomes.map((o) => (
                      <div
                        key={o.outcome}
                        className="flex justify-between gap-2"
                      >
                        <span>{label("outcomes", o.outcome)}</span>
                        <span className="tabular-nums">{number(o.count)}</span>
                      </div>
                    ))}
                    {rejected.length ? (
                      <>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {t("rejected")}
                        </div>
                        {rejected.map((r) => (
                          <div
                            key={r.reason}
                            className="flex justify-between gap-2 text-xs text-muted-foreground"
                          >
                            <span>{label("rejections", r.reason)}</span>
                            <span className="tabular-nums">
                              {number(r.count)}
                            </span>
                          </div>
                        ))}
                      </>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

RadarHealthPage.path = "/radar_health";
